/**
 * src/components/teacher-dashboard-components/theme/components/Modal.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
import React, { useEffect, useId, useRef } from 'react';
import styled from 'styled-components';
import { useTheme } from '../ThemeContext';
import { getColor, createTransition } from '../themeUtils';

const scrollLocks = new Set();
let previousBodyOverflow;

const lockBodyScroll = () => {
  const token = {};
  if (scrollLocks.size === 0) {
    previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  scrollLocks.add(token);
  return () => {
    scrollLocks.delete(token);
    if (scrollLocks.size === 0) document.body.style.overflow = previousBodyOverflow;
  };
};

const isAvailable = (element) => {
  if (!element?.isConnected || element.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
  for (let current = element; current instanceof HTMLElement; current = current.parentElement) {
    const style = window.getComputedStyle(current);
    if (style.display === 'none' || ['hidden', 'collapse'].includes(style.visibility)) return false;
  }
  return true;
};

const isTopDialog = (dialog) => {
  const dialogs = Array.from(document.querySelectorAll('[role="dialog"][aria-modal="true"]')).filter(isAvailable);
  return dialogs[dialogs.length - 1] === dialog;
};

const getTabStops = (dialog) => Array.from(dialog.querySelectorAll(
  'a[href], button, input:not([type="hidden"]), select, textarea, [tabindex], [contenteditable="true"]'
)).filter((element) => element.tabIndex >= 0 && !element.matches(':disabled')
  && element.getAttribute('aria-disabled') !== 'true' && isAvailable(element))
  .sort((a, b) => (a.tabIndex || Number.MAX_SAFE_INTEGER) - (b.tabIndex || Number.MAX_SAFE_INTEGER));

const focusWithoutScroll = (element) => element?.focus({ preventScroll: true });

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(6, 55, 91, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${props => props.$zIndex || 1000};
  padding: ${props => props.theme.spacing.md};
  ${createTransition('opacity', 'fast')};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.sm};
  `}
`;

const ModalContainer = styled.div`
  background: ${getColor('neutral.white')};
  border: 1px solid #d8e5ee;
  border-radius: 1rem;
  box-shadow: 0 20px 44px rgba(17, 63, 103, 0.18);
  width: 100%;
  max-width: ${props => props.$maxWidth || '500px'};
  max-height: 90vh;
  overflow-y: auto;
  ${createTransition('transform', 'normal')};
  
  ${props => props.theme.isMobile && `
    max-width: 100%;
    max-height: 95vh;
    border-radius: 0.875rem;
  `}
`;

const ModalHeader = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${getColor('neutral.medium')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
  `}
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${props => props.theme.typography.fontFamily.primary};
  font-size: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h3.fontSize : 
    props.theme.typography.desktop.h3.fontSize};
  font-weight: ${props => props.theme.isMobile ? 
    props.theme.typography.mobile.h3.fontWeight : 
    props.theme.typography.desktop.h3.fontWeight};
  color: ${getColor('primary.blue')};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  width: 44px;
  height: 44px;
  padding: ${props => props.theme.spacing.sm};
  cursor: pointer;
  color: ${getColor('neutral.dark')};
  border-radius: ${props => props.theme.borderRadius.sm};
  ${createTransition('background', 'fast')};
  
  &:hover {
    background: ${getColor('neutral.light')};
  }
  
  &:focus-visible {
    outline: 2px solid ${getColor('primary.skyBlue')};
    outline-offset: 2px;
  }
`;

const ModalContent = styled.div`
  padding: ${props => props.theme.spacing.lg};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
  `}
`;

const ModalFooter = styled.div`
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${getColor('neutral.medium')};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing.md};
  
  ${props => props.theme.isMobile && `
    padding: ${props.theme.spacing.md};
    flex-direction: column-reverse;
    gap: ${props.theme.spacing.sm};
  `}
`;

/**
 * Modal.
 *
 * React component or UI helper used to render and manage local UI interactions.
 */
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth,
  showCloseButton = true,
  closeOnOverlayClick = true,
  className,
  footer,
  zIndex,
  ...props
}) => {
  const theme = useTheme();
  const modalRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;
    const dialog = modalRef.current;
    const opener = document.activeElement;
    const releaseScroll = lockBodyScroll();
    if (isTopDialog(dialog)) focusWithoutScroll(dialog);

    const handleFocus = (event) => {
      if (isTopDialog(dialog) && !dialog.contains(event.target)) focusWithoutScroll(dialog);
    };
    const handleKeyDown = (event) => {
      // Bubble-phase handling respects nested dialogs and caller-prevented keys.
      if (event.defaultPrevented || !isTopDialog(dialog)) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseRef.current?.();
      } else if (event.key === 'Tab') {
        // Move explicitly: WebKit may otherwise skip native controls depending on
        // keyboard preferences and leave the dialog before reaching its boundary.
        event.preventDefault();
        const controls = getTabStops(dialog);
        const current = controls.indexOf(document.activeElement);
        const step = event.shiftKey ? -1 : 1;
        const start = current < 0 ? (event.shiftKey ? 0 : -1) : current;
        for (let offset = 1; offset <= controls.length; offset += 1) {
          const index = (start + step * offset + controls.length) % controls.length;
          focusWithoutScroll(controls[index]);
          if (document.activeElement === controls[index]) return;
        }
        focusWithoutScroll(dialog);
      }
    };
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('keydown', handleKeyDown);
      releaseScroll();
      if (opener !== document.body && isAvailable(opener)) focusWithoutScroll(opener);
    };
  }, [isOpen]);
  
  if (!isOpen) return null;
  
    /**
   * Handle Overlay Click.
   *
   * React component or UI helper used to render and manage local UI interactions.
   */
   const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose?.();
    }
  };
  
  return (
    <ModalOverlay theme={theme} $zIndex={zIndex} onClick={handleOverlayClick}>
      <ModalContainer
        ref={modalRef}
        theme={theme}
        $maxWidth={maxWidth}
        className={className}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        {...props}
      >
        {(title || showCloseButton) && (
          <ModalHeader theme={theme}>
            {title && <ModalTitle id={titleId} theme={theme}>{title}</ModalTitle>}
            {showCloseButton && (
              <CloseButton type="button" theme={theme} onClick={onClose} aria-label="Close">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </CloseButton>
            )}
          </ModalHeader>
        )}
        
        <ModalContent theme={theme}>
          {children}
        </ModalContent>
        
        {footer && (
          <ModalFooter theme={theme}>
            {footer}
          </ModalFooter>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

export default Modal;
